import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose
} from '../../ui/drawer'
import { Button } from '../../ui/button'
import MultiSelectStudent from './MultiSelectStudent/MultiSelectStudent'
import { requestCreator } from '../../../utilities/requests'

const { get: getStudents, cancel: cancelStudentsRequests } = requestCreator()

function StudentsDrawer({ open, onOpenChange, students, selectedSkillLevels, onChange, facilityId }) {
  const [isLoading, setIsLoading] = useState(false)
  const [availableStudents, setAvailableStudents] = useState(students)
  const studentsRef = useRef(students)

  useEffect(() => {
    studentsRef.current = students
  }, [students])

  useEffect(() => {
    if (open) {
      setIsLoading(true)
      const currentStudents = studentsRef.current
      getStudents(`/api/facilities/${facilityId}/students`, {
        params: {
          skillLevels: selectedSkillLevels.map((skillLevel) => skillLevel.id),
          include: 'current_skill_level',
          fields: {
            student: 'fullName,preferredName,cloudinaryPhotoId,current_skill_level,age',
            skill_level: 'name'
          }
        }
      }).then(({ records: studentList }) => {
        const updatedStudents = studentList.map((student) => {
          const existingStudent = currentStudents.find((s) => s.id === student.id)
          return {
            ...student,
            selected: existingStudent ? existingStudent.selected : false,
            isLocked: existingStudent ? existingStudent.isLocked : false
          }
        })
        setAvailableStudents(updatedStudents)
        setIsLoading(false)
      })
    }

    return cancelStudentsRequests
  }, [open, selectedSkillLevels, facilityId])

  function handleStudentsChange(newStudents) {
    const updatedStudents = newStudents.map((student) => {
      const previousStudent = availableStudents.find((s) => s.id === student.id)
      const isStudentLocked = student.isLocked || (previousStudent && previousStudent.selected !== student.selected)
      return {
        ...student,
        isLocked: isStudentLocked
      }
    })
    setAvailableStudents(updatedStudents)
    onChange(updatedStudents)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Manage Students</DrawerTitle>
          <DrawerDescription>
            If you must add students manually, you should add them to the package for this class instead. Use this
            section only for exceptional cases.
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 p-6 overflow-y-auto">
          <MultiSelectStudent value={availableStudents} onChange={handleStudentsChange} loading={isLoading} />
        </div>

        <DrawerFooter className="border-t border-gray-200">
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              Done
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

StudentsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  students: PropTypes.array.isRequired,
  selectedSkillLevels: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  facilityId: PropTypes.string.isRequired
}

export default StudentsDrawer
